import { useEffect, useMemo, useState } from 'react'
import './StoreLayoutSchematic.css'

/** Incomplete first; among found, most recently completed first (matches GroceryList). */
function sortItemsForLayout(items, optSet) {
  return [...items].sort((a, b) => {
    const fa = !!(a.completed || (a.id && optSet.has(a.id)))
    const fb = !!(b.completed || (b.id && optSet.has(b.id)))
    if (fa && !fb) return 1
    if (!fa && fb) return -1
    if (fa && fb) return (b.completedAt || 0) - (a.completedAt || 0)
    return 0
  })
}

/**
 * StoreLayoutSchematic - Interactive store layout with grocery items overlaid
 * 
 * @param {Object} props
 * @param {Array} props.groceryItems - Array of grocery items with aisle_id, aisle_name, item, completed, etc.
 * @param {Function} props.onItemClick - Optional callback when an item is clicked (itemId) => void
 * @param {string} props.storeName - Name of the store (e.g., "Shaw's")
 * @param {string} props.storeLocation - Location of the store (e.g., "Peterborough NH")
 * @param {string} props.displayMode - "pins" (compact pin markers) | "lists" (inline item lists)
 * @param {boolean} props.showHeader - Whether to show the internal schematic header (title/subtitle)
 */
export default function StoreLayoutSchematic({ 
  groceryItems = [], 
  onItemClick = null,
  storeName = "Shaw's",
  storeLocation = "Peterborough NH",
  displayMode = "pins",
  showHeader = true
}) {
  const [activePin, setActivePin] = useState(null) // { title, subtitle, items, anchorRect? }
  const [isMobile, setIsMobile] = useState(false)
  // Optimistic "marked found" IDs so modal UI updates immediately before parent state propagates
  const [optimisticFoundIds, setOptimisticFoundIds] = useState([])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

    const mq = window.matchMedia('(max-width: 768px)')
    const update = () => setIsMobile(Boolean(mq.matches))
    update()

    // Safari < 14 uses addListener/removeListener
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update)
      return () => mq.removeEventListener('change', update)
    }

    mq.addListener(update)
    return () => mq.removeListener(update)
  }, [])

  useEffect(() => {
    if (!activePin || !isMobile) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setActivePin(null)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [activePin, isMobile])

  // When props confirm an item is completed, stop treating it as optimistic
  useEffect(() => {
    const confirmed = groceryItems.filter(it => it.completed && it.id).map(it => it.id)
    if (confirmed.length === 0) return
    setOptimisticFoundIds(prev => {
      const remove = new Set(confirmed)
      const next = prev.filter(id => !remove.has(id))
      return next.length === prev.length ? prev : next
    })
  }, [groceryItems])

  const optimisticFoundSet = useMemo(() => new Set(optimisticFoundIds), [optimisticFoundIds])

  // Group all grocery items by aisle (found items stay visible with strikethrough, like the main list)
  const itemsByAisle = useMemo(() => {
    const grouped = {}
    const optSet = new Set(optimisticFoundIds)

    groceryItems.forEach(item => {
      let aisleId = item.aisle_id
      if (!aisleId) {
        aisleId = 'unmapped'
      } else {
        aisleId = String(aisleId).trim().toUpperCase().replace(/\s+/g, '').replace(/-/g, '')
      }

      if (!grouped[aisleId]) {
        grouped[aisleId] = {
          aisle_id: item.aisle_id,
          aisle_name: item.aisle_name || 'Unknown Aisle',
          items: []
        }
      }
      grouped[aisleId].items.push(item)
    })

    Object.keys(grouped).forEach((key) => {
      grouped[key].items = sortItemsForLayout(grouped[key].items, optSet)
    })

    return grouped
  }, [groceryItems, optimisticFoundIds])

  // Normalize aisle ID for matching
  const normalizeAisleId = (aisleId) => {
    if (!aisleId) return null
    return String(aisleId).trim().toUpperCase().replace(/\s+/g, '').replace(/-/g, '')
  }

  // Get items for a specific aisle (handles normalized matching)
  const getItemsForAisle = (aisleId) => {
    const normalized = normalizeAisleId(aisleId)
    // Try exact match first
    if (itemsByAisle[normalized]) {
      return itemsByAisle[normalized].items
    }
    // Try case-insensitive match
    const found = Object.keys(itemsByAisle).find(key => 
      normalizeAisleId(key) === normalized
    )
    return found ? itemsByAisle[found].items : []
  }

  // Merge items across multiple aisle IDs (dedup by item.id)
  const getItemsForAnyAisle = (aisleIds = []) => {
    const merged = []
    const seen = new Set()
    const optSet = new Set(optimisticFoundIds)
    aisleIds.forEach((id) => {
      getItemsForAisle(id).forEach((item) => {
        const key = item?.id ?? `${item?.item ?? 'unknown'}-${item?.aisle_id ?? ''}`
        if (seen.has(key)) return
        seen.add(key)
        merged.push(item)
      })
    })
    return sortItemsForLayout(merged, optSet)
  }

  // Check if aisle has items
  const hasItems = (aisleId) => {
    return getItemsForAisle(aisleId).length > 0
  }

  const isItemDisplayFound = (item) =>
    !!(item?.completed || (item?.id && optimisticFoundSet.has(item.id)))

  // Toggle found state; optimistic IDs update immediately before parent props catch up
  const handleItemCheckboxClick = (e, item) => {
    e.stopPropagation()
    if (!item?.id) return
    const currentlyFound = !!(item.completed || optimisticFoundSet.has(item.id))
    setOptimisticFoundIds((prev) => {
      if (currentlyFound) return prev.filter((id) => id !== item.id)
      return prev.includes(item.id) ? prev : [...prev, item.id]
    })
    if (onItemClick) onItemClick(item.id)
  }

  const renderItemsList = (items) => {
    if (!items || items.length === 0) {
      return (
        <div className="store-layout-pin-panel-empty">
          No items in this area.
        </div>
      )
    }
    return (
      <div className="store-layout-pin-panel-items" role="list">
        {items.map((item) => {
          const found = isItemDisplayFound(item)
          return (
            <div
              key={item.id}
              className={`store-layout-item${found ? ' store-layout-item--found' : ''}`}
              role="listitem"
            >
              <button
                type="button"
                className={`store-layout-item-toggle${found ? ' store-layout-item-toggle--checked' : ''}`}
                onClick={(e) => handleItemCheckboxClick(e, item)}
                title={found ? 'Mark as not found' : 'Mark as found'}
                aria-label={
                  found
                    ? `Mark ${item.item} as not found`
                    : `Mark ${item.item} as found`
                }
                aria-pressed={found}
              />
              <span className="store-layout-item-name" title={item.item}>
                {item.item}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  const PinMarker = ({ count, title, subtitle, aisleIds }) => {
    const has = (count ?? 0) > 0
    const label = subtitle ? `${title} (${subtitle})` : title
    return (
      <button
        type="button"
        className={`store-layout-pin ${has ? 'store-layout-pin--active' : 'store-layout-pin--empty'}`}
        aria-label={has ? `${label}: ${count} items` : `${label}: no items`}
        title={has ? `${count} items` : 'No items'}
        disabled={!has}
        onClick={(e) => {
          e.stopPropagation()
          if (!has) return
          const rect = e.currentTarget?.getBoundingClientRect?.()
          setActivePin((prev) => {
            const same = prev?.title === title && prev?.subtitle === subtitle
            return same ? null : { title, subtitle, aisleIds: aisleIds || [], anchorRect: rect || null }
          })
        }}
      >
        <svg
          className="store-layout-pin-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          {/* Universal map pin / marker shape */}
          <path d="M12 2c-3.866 0-7 3.134-7 7 0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7z" />
        </svg>
        <span className="store-layout-pin-count" aria-hidden="true">{count}</span>
      </button>
    )
  }

  // Aisle data structure matching the HTML
  const aisles = [
    { id: 'A2', name: 'Beer & Wine' },
    { id: 'A3', name: 'Juice & Coffee' },
    { id: 'A4', name: 'Breakfast & Baking' },
    { id: 'A5', name: 'Aisle 5' },
    { id: 'A6', name: 'Canned Foods & Pasta' },
    { id: 'A7', name: 'Snacks & Crackers' },
    { id: 'A8', name: 'Soda & Water' },
    { id: 'A9', name: 'Health & Personal Care' },
    { id: 'A10', name: 'Household & Cleaning' },
    { id: 'A12', name: 'Frozen Meals' },
    { id: 'A13', name: 'Frozen Desserts' },
    { id: 'A14', name: 'Dairy' },
  ]

  // Zone <-> aisle mapping (these are store-layout IDs, not the decorative zone IDs)
  const produceItems = getItemsForAnyAisle(['PFZ'])
  const bakeryItems = getItemsForAnyAisle(['A17'])
  const fishItems = getItemsForAnyAisle(['A16'])
  const meatItems = getItemsForAnyAisle(['A15'])

  return (
    <div className="store-layout-schematic">
      <div className="store-layout-schematic-inner">
        {showHeader && (
          <div className="store-layout-schematic-header">
            <h1 className="store-layout-schematic-title">🛒 {storeName} Store Layout</h1>
            <p className="store-layout-schematic-subtitle">{storeLocation} - Store Schematic</p>
          </div>
        )}
        
        <div className="store-layout">
          <div className="store-layout-top">
            <div className={`store-layout-zone store-layout-zone--bakery ${displayMode === 'lists' && bakeryItems.length > 0 ? 'has-items' : ''}`}>
              <div className="store-layout-zone-header">
                <h2 className="store-layout-zone-title">Bakery</h2>
                <div className="store-layout-zone-right">
                  <span className="store-layout-zone-meta">A17</span>
                  {displayMode === 'pins' && bakeryItems.length > 0 && (
                    <PinMarker
                      count={bakeryItems.length}
                      title="Bakery"
                      subtitle="A17"
                      aisleIds={['A17']}
                    />
                  )}
                </div>
              </div>
              {displayMode === 'lists' && renderItemsList(bakeryItems)}
            </div>

            <div className={`store-layout-zone store-layout-zone--produce ${displayMode === 'lists' && produceItems.length > 0 ? 'has-items' : ''}`}>
              <div className="store-layout-zone-header">
                <h2 className="store-layout-zone-title">Produce Front Zone</h2>
                <div className="store-layout-zone-right">
                  <span className="store-layout-zone-meta">PFZ</span>
                  {displayMode === 'pins' && produceItems.length > 0 && (
                    <PinMarker
                      count={produceItems.length}
                      title="Produce Front Zone"
                      subtitle="PFZ"
                      aisleIds={['PFZ']}
                    />
                  )}
                </div>
              </div>
              {displayMode === 'lists' && renderItemsList(produceItems)}
            </div>

            <div className="store-layout-zone store-layout-zone--entrance">
              <div>
                <div className="store-layout-entrance-icon" aria-hidden="true">🚪</div>
                <div className="store-layout-entrance-text">Entrance</div>
              </div>
            </div>
          </div>
          
          <div className="store-layout-aisles">
            <div className="store-layout-back-sections">
              <div className={`store-layout-back-section store-layout-back-section--fish ${displayMode === 'lists' && fishItems.length > 0 ? 'has-items' : ''}`}>
                <div className="store-layout-zone-header">
                  <span className="store-layout-aisle-number">A16</span>
                  <span className="store-layout-aisle-name" title="Fresh Fish">Fresh Fish</span>
                  <div className="store-layout-zone-right">
                    {displayMode === 'pins' && fishItems.length > 0 && (
                      <PinMarker
                        count={fishItems.length}
                        title="Fresh Fish"
                        subtitle="A16"
                        aisleIds={['A16']}
                      />
                    )}
                  </div>
                </div>
                {displayMode === 'lists' && renderItemsList(fishItems)}
              </div>

              <div className={`store-layout-back-section store-layout-back-section--meat ${displayMode === 'lists' && meatItems.length > 0 ? 'has-items' : ''}`}>
                <div className="store-layout-zone-header">
                  <span className="store-layout-aisle-number">A15</span>
                  <span className="store-layout-aisle-name" title="Fresh Meat">Fresh Meat</span>
                  <div className="store-layout-zone-right">
                    {displayMode === 'pins' && meatItems.length > 0 && (
                      <PinMarker
                        count={meatItems.length}
                        title="Fresh Meat"
                        subtitle="A15"
                        aisleIds={['A15']}
                      />
                    )}
                  </div>
                </div>
                {displayMode === 'lists' && renderItemsList(meatItems)}
              </div>
            </div>
            
            <div className="store-layout-main-aisles">
              {aisles.map(aisle => {
                const items = getItemsForAisle(aisle.id)
                const hasItemsInAisle = hasItems(aisle.id)
                
                return (
                  <div 
                    key={aisle.id} 
                    className={`store-layout-aisle ${hasItemsInAisle ? 'has-items' : ''}`}
                  >
                    <div className="store-layout-aisle-header">
                      <span className="store-layout-aisle-number">{aisle.id}</span>
                      <span className="store-layout-aisle-name" title={aisle.name}>{aisle.name}</span>
                      {displayMode === 'pins' && items.length > 0 && (
                        <PinMarker
                          count={items.length}
                          title={aisle.name}
                          subtitle={aisle.id}
                          aisleIds={[aisle.id]}
                        />
                      )}
                    </div>
                    {displayMode === 'lists' && hasItemsInAisle && renderItemsList(items)}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {displayMode === 'pins' && activePin && (() => {
          const activePinItems = Array.isArray(activePin.aisleIds) && activePin.aisleIds.length
            ? getItemsForAnyAisle(activePin.aisleIds)
            : []
          return (
          <div
            className={`store-layout-pin-popover-overlay ${isMobile ? 'store-layout-pin-popover-overlay--mobile' : 'store-layout-pin-popover-overlay--desktop'}`}
            role="presentation"
            onClick={() => setActivePin(null)}
          >
            <div
              className={`store-layout-pin-popover ${isMobile ? 'store-layout-pin-popover--mobile' : 'store-layout-pin-popover--desktop'}`}
              role="dialog"
              aria-modal={isMobile ? 'true' : 'false'}
              aria-label="Aisle items"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="store-layout-pin-panel-header">
                <div className="store-layout-pin-panel-title">
                  <div className="store-layout-pin-panel-title-text">{activePin.title}</div>
                  {activePin.subtitle && (
                    <div className="store-layout-pin-panel-subtitle">{activePin.subtitle}</div>
                  )}
                </div>
                <button
                  type="button"
                  className="store-layout-pin-panel-close"
                  onClick={() => setActivePin(null)}
                  aria-label="Close"
                  title="Close"
                >
                  ×
                </button>
              </div>
              <div className="store-layout-pin-panel-meta">
                <span className="badge badge-count">{activePinItems.length}</span>
                <span>items</span>
              </div>
              {renderItemsList(activePinItems)}
            </div>
          </div>
          )
        })()}
        
        {/* Unmapped items section */}
        {itemsByAisle['unmapped'] && itemsByAisle['unmapped'].items.length > 0 && (
          <div className="store-layout-unmapped">
            <h3 className="store-layout-unmapped-title">⚠️ Items Without Aisle Assignment</h3>
            <div className="store-layout-unmapped-list">
              {renderItemsList(itemsByAisle['unmapped'].items)}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

